import {
  type GetArtifactResp,
  type HeavyArtifactResponse,
  isActionableArtifact,
  isUnansweredForm,
  type UserResponseArtifact,
} from "@/types/artifacts.ts";
import { userMessageTypes } from "@/types/messages.ts";
import type { GetThreadResp } from "@/types/thread.ts";
import type { ThreadEventItem } from "@/types/threadEvents.ts";
import { isResponseMessage } from "./anchoredBiscuits.ts";

const USER_MESSAGE_TYPES: ReadonlySet<string> = new Set(userMessageTypes.options);

/**
 * An artifact surfaced in the omni-artifact overlay. Heavy artifacts awaiting an
 * action (draft emails, invitations, …) plus light `userResponse` form artifacts
 * whose questions are still unanswered — both step through the same carousel.
 */
export type OmniArtifact = HeavyArtifactResponse | UserResponseArtifact;

// Re-exported so the omni surfaces keep a single import site; the predicate
// itself lives in the shared types so the server look-back uses the same gate.
export { isUnansweredForm };

/**
 * Whether the thread as a whole has a decision sitting with the user — the
 * signal that lets an omni review *open* at all.
 *
 * Deliberately not `getThreadStatus(thread)`. That answers a different
 * question — which badge to show — and to answer it, `deriveCardStatus`
 * collapses `isPaused` and `activityState` into one enum with pause winning.
 * Those two are not alternatives: a received invitation is created paused
 * *and* waiting on the user, because pause stops the agent and the RSVP is the
 * user acting. Asking the badge hid the card on every such thread (BIG-7601).
 *
 * `activityState` is the same backend signal the server look-back
 * (`threadHasPendingApproval`) sets at a turn's end, so this keeps the property
 * the gate was introduced for (BIG-5581) — a server-owned answer rather than a
 * guess derived from the message list, which also bounds the staleness a turn
 * whose user message has fallen outside the client's window would otherwise
 * surface. Both terminal statuses are excluded — `completed`, the archived
 * thread that asks nothing of anyone, and `dead`, which an invitation reaches
 * when its meeting slips past. `markThreadAsDead` only resets `activityState`
 * for a *running* thread, so an expired paused invitation keeps
 * `waitingForUser` next to an RSVP nobody can act on any more.
 *
 * Takes only the two fields it reads. `card` is optional on the wire and
 * `isPaused` is none of this predicate's business, so neither is required of a
 * caller or a fixture.
 */
export function threadIsAwaitingUserDecision(
  thread: Pick<GetThreadResp, "status" | "activityState"> | undefined,
) {
  if (!thread) return false;
  return (
    thread.status !== "completed" &&
    thread.status !== "dead" &&
    thread.activityState === "waitingForUser"
  );
}

/**
 * The heavy artifacts awaiting user action in the **current agent turn**, in
 * chronological order. This is the single source of truth that fans out to
 * every omni-artifact surface (web's overlay carousel + bottom inline list, and
 * native's omnibox biscuit) plus the inline-collapse decision — deriving them
 * all from one value keeps the surfaces from ever disagreeing.
 *
 * We walk `items` backwards and stop at the turn boundary: the last user
 * message, or the reply before the current one, whichever is nearer (see
 * {@link selectTurnOmniCards}). A message that carries non-pending artifacts no
 * longer suppresses the result — we collect across every message in the turn,
 * not just the first artifact-bearing one (the bug behind BIG-5269).
 *
 * Presentation-agnostic on purpose: no active index, no carousel concepts. The
 * scope (current turn) is the only product knob here; widening to thread-wide
 * would be removing the `break`s.
 */
export function selectOmniArtifacts(items: ThreadEventItem[]): OmniArtifact[] {
  return selectTurnOmniCards(items).filter(isActionableArtifact);
}

/**
 * Whether an artifact can be shown as an omni card at all, in any state — the
 * shape test behind {@link isActionableArtifact}, without its "still waiting"
 * condition.
 *
 * Type is not a criterion. A paused connector tool call and the `userResponse`
 * form asking about it are one decision across two artifacts, and the queue
 * carries both: the form holds the Yes/No control, the call holds the server,
 * tool and arguments that say what is being approved. BIG-7892 had excluded
 * the call, which left the control on a card showing none of that.
 */
function isOmniCard(artifact: GetArtifactResp): artifact is OmniArtifact {
  return artifact.isHeavy || artifact.artifactType === "userResponse";
}

/**
 * Whether an omni card carries the payload its surface draws from — the shape
 * test {@link isOmniCard} can't make, because what a card renders *from* is
 * stamped per-status by the server rather than implied by the artifact's type.
 *
 * A card that fails this renders nothing: `ArtifactBiscuit` returns `null`
 * without a `biscuit`, and the stepped form card returns `null` with no field
 * to step to. That is worse than an empty slot, because the composer's
 * follow-up Omnibox is a child of whichever card has the surface — so a card
 * the surface cannot draw takes the thread's only text box down with it
 * (BIG-7439).
 *
 * The server stamps `biscuit` only while a heavy artifact is awaiting a
 * decision (`buildArtifactSummary`), so every settled one fails here. That is
 * the intent, not an accident of the rollout: once an artifact is decided the
 * inline thread card is meant to be its only surface, with the omnibox left
 * alone.
 *
 * Deliberately narrower than {@link isActionableArtifact}: an *answered* form
 * is not awaiting anything, but it still renders — as `OmniArtifactFormBiscuit`
 * — and opening one from the feed is a destination the feed offers.
 */
export function hasOmniCardContent(artifact: OmniArtifact): boolean {
  if (artifact.isHeavy) return !!artifact.biscuit;
  if (!artifact.form) return false;
  // Answered forms render their answers; an unanswered one is a question at a
  // time, and a form with no questions has no first step to show.
  return artifact.form.values !== undefined || artifact.form.fields.length > 0;
}

/**
 * Every artifact in the current turn that an omni surface can render, decided or
 * not, in chronological order. {@link selectOmniArtifacts} is this narrowed to
 * the ones still awaiting the user.
 *
 * The wider list exists because a review outlives its own decisions: web holds a
 * card in the carousel after the user sends it (see `mergeReviewArtifacts`), and
 * a held card has to keep re-reading itself here or it renders frozen at the
 * moment it stopped being actionable — still offering Send for an email already
 * on its way.
 *
 * The turn ends at the last user message, or at the reply before the newest
 * one, whichever is nearer. A reply is a message okthx answered with
 * (`isResponseMessage`, the feed's own test), and it has to bound the turn
 * too: an SMS or email inbound is an assistant-type `externalCommunication`,
 * so on a user message alone one turn runs across several exchanges and every
 * reply re-shows the cards the one before it already carried. The server's
 * `getCurrentTurnMessages` draws the same line, so the thread's
 * `activityState` and this list agree on what is still current.
 */
export function selectTurnOmniCards(items: ThreadEventItem[]): OmniArtifact[] {
  const collected: OmniArtifact[] = [];
  // Walking newest→oldest, the first reply met is the current one and belongs
  // to the turn; the next one is the previous turn's answer and ends the walk.
  let pastCurrentReply = false;

  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item?.type !== "message") continue;
    // An aborted message renders nothing, so it is neither a reply nor a card.
    if (item.message.status === "aborted") continue;
    if (USER_MESSAGE_TYPES.has(item.message.type)) break;
    const artifacts = item.message.artifacts ?? [];
    if (isResponseMessage(artifacts)) {
      if (pastCurrentReply) break;
      pastCurrentReply = true;
    }
    // Prepend each older message's picks (kept in their own forward order) to
    // build a chronological list in one pass.
    collected.unshift(...artifacts.filter(isOmniCard));
  }

  // Dedup by id (defensive against the session/historical merge surfacing the
  // same artifact twice), preserving first chronological occurrence.
  const seen = new Set<string>();
  return collected.filter((a) => {
    if (seen.has(a.id)) return false;
    seen.add(a.id);
    return true;
  });
}
